/**
 * Campaign Monitor Service
 * Monitors campaign generation status using EventSource (Server-Sent Events)
 * @module services/websocket/campaignMonitor
 */

export interface CampaignMediaData {
  id: string;
  accountId: string;
  restaurantId: string;
  taskType: string;
  lifecycle: string;
  stage: string;
  message: string;
  metadata: any;
  createdAt: string;
  updatedAt: string;
  percentCompleted: number;
}

export interface CampaignMonitorEvent {
  type: 'connected' | 'progress' | 'done' | 'error';
  message?: string;
  data?: CampaignMediaData;
  ideaId?: number;
  ideaType?: string;
  restaurantId?: string;
  timestamp?: number;
}

export type CampaignStatusCallback = (event: CampaignMonitorEvent) => void;

/**
 * Monitor campaign generation status using Server-Sent Events
 * @param restaurantId - The restaurant ID
 * @param ideaIdOrTaskId - The idea ID (number) or task ID (string) to monitor
 * @param ideaType - The idea type (e.g., 'MANUAL_IDEA') - optional for taskId
 * @param onStatusUpdate - Callback for status updates
 * @param onError - Callback for errors
 * @returns Cleanup function to close the connection
 */
export const monitorCampaignGeneration = (
  restaurantId: string,
  ideaIdOrTaskId: number | string,
  ideaType: string | undefined,
  onStatusUpdate: CampaignStatusCallback,
  onError?: (error: Error) => void
): (() => void) => {
  const baseUrl = import.meta.env.VITE_TRIGGER_URL || '';
  
  // Backend uses a single 'id' parameter for monitoring (works with both taskId and ideaId)
  const url = `https://stream.snapmenu.ai/v1/task/monitor/stream?id=${ideaIdOrTaskId}&restaurantId=${restaurantId}`;
  
  const eventSource = new EventSource(url);

  eventSource.onopen = () => {
    console.log('[Campaign Monitor] Connection established for id:', ideaIdOrTaskId);
  };

  eventSource.addEventListener('connected', (event) => {
    console.log('[Campaign Monitor] Connected event:', (event as MessageEvent).data);
    try {
      const data = JSON.parse((event as MessageEvent).data);
      onStatusUpdate({
        type: 'connected',
        message: data.message,
        ideaId: data.ideaId,
        ideaType: data.ideaType,
        restaurantId: data.restaurantId,
        timestamp: data.timestamp,
      });
    } catch (err) {
      console.error('[Campaign Monitor] Failed to parse connected event:', err);
    }
  });

  eventSource.addEventListener('task-update', (event) => {
    console.log('[Campaign Monitor] Task update event:', (event as MessageEvent).data);
    try {
      const parsed = JSON.parse((event as MessageEvent).data);
      const data: CampaignMediaData = Array.isArray(parsed) ? parsed[0] : parsed;
      
      onStatusUpdate({
        type: 'progress',
        data: data,
      });
    } catch (err) {
      console.error('[Campaign Monitor] Failed to parse task-update event:', err);
    }
  });

  eventSource.addEventListener('done', (event) => {
    console.log('[Campaign Monitor] Done event:', (event as MessageEvent).data);
    try {
      const eventData = JSON.parse((event as MessageEvent).data);
      onStatusUpdate({
        type: 'done',
        message: eventData.message,
        data: eventData.data,
      });
      console.log('[Campaign Monitor] Task monitoring completed, closing connection');
      eventSource.close();
    } catch (err) {
      console.error('[Campaign Monitor] Failed to parse done event:', err);
      eventSource.close();
    }
  });

  eventSource.addEventListener('error', (event) => {
    console.error('[Campaign Monitor] Server error event:', event);
    onStatusUpdate({
      type: 'error',
      message: 'Error occurred during campaign generation monitoring',
    });
  });

  eventSource.onerror = (error) => {
    console.error('[Campaign Monitor] Connection error occurred', error);
    if (onError) {
      onError(new Error('Campaign monitor connection error'));
    }
  };

  return () => {
    console.log('[Campaign Monitor] Closing connection for id:', ideaIdOrTaskId);
    eventSource.close();
  };
};

export const isCampaignGenerationComplete = (data: CampaignMediaData): boolean => {
  return data.lifecycle === 'Completed' || data.percentCompleted === 100;
};

export const hasCampaignGenerationFailed = (data: CampaignMediaData): boolean => {
  return data.lifecycle === 'Failed';
};

export const formatCampaignStatus = (stage: string): string => {
  if (!stage) return '';
  return stage.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
};
